import React from 'react';
import { X, XCircle, BookOpen, RefreshCw, AlertTriangle, HelpCircle } from 'lucide-react';

const TaskFailureModal = ({
    isOpen,
    onClose,
    task,
    score,
    passingScore,
    missedTopics = [],
    allowRetakes,
    onRetake,
    onReviewMaterial,
    onBackToDashboard
}) => {
    if (!isOpen) return null;

    const pointsNeeded = passingScore - score;

    return (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-500 to-red-600 p-8 text-white text-center relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <XCircle className="w-10 h-10" />
                    </div>

                    <h2 className="text-2xl font-bold mb-2">Not Quite There</h2>
                    <p className="text-white/90">You need {pointsNeeded}% more to pass</p>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Score Display */}
                    <div className="bg-red-50 rounded-xl p-6 text-center">
                        <div className="flex items-center justify-center gap-4">
                            <div>
                                <p className="text-sm text-gray-500 mb-1">Your Score</p>
                                <p className="text-4xl font-bold text-red-600">{score}%</p>
                            </div>
                            <div className="w-px h-16 bg-red-200" />
                            <div>
                                <p className="text-sm text-gray-500 mb-1">Passing Score</p>
                                <p className="text-4xl font-bold text-gray-600">{passingScore}%</p>
                            </div>
                        </div>
                    </div>

                    {/* Missed Topics */}
                    {missedTopics.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <HelpCircle className="w-5 h-5 text-amber-500" />
                                Areas to Review
                            </h3>
                            <div className="space-y-2">
                                {missedTopics.map((topic, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm"
                                    >
                                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                        <span className="text-amber-800">{topic}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Encouragement */}
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-gray-600">
                            Don't worry! Take some time to review the material and try again when you're ready.
                        </p>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="px-6 pb-6 space-y-3">
                    {allowRetakes && (
                        <button
                            onClick={onRetake}
                            className="w-full py-3 px-4 bg-primary hover:bg-primary-hover text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                        >
                            <RefreshCw className="w-5 h-5" />
                            Retake Quiz
                        </button>
                    )}

                    {onReviewMaterial && (
                        <button
                            onClick={onReviewMaterial}
                            className="w-full py-3 px-4 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                        >
                            <BookOpen className="w-5 h-5" />
                            Review Material
                        </button>
                    )}

                    <button
                        onClick={onBackToDashboard}
                        className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaskFailureModal;
